// initial modules:
import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import {
    BrowserRouter as Router,
    Switch,
    Route,
    Redirect,
    Link,
} from 'react-router-dom';

// Utils:
import { ErrorBoundary, useErrorHandler } from 'react-error-boundary';
import { handleFetchError } from 'utilities/utils';
import { findTopicByUrlPath } from 'utilities/topicConfigUtils';
import { TopicDrawerNavigationContext } from 'utilities/topicConfigContext';
import { LicenseInfo } from '@mui/x-data-grid-pro';

// style:
import './App.css';
import Grid from '@mui/material/Grid';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { ThemeProvider } from '@mui/styles';
import { StyledEngineProvider } from '@mui/material/styles';
import muiTheme from './theme';

// Components:
import HealthTopic from './components/MainContent/HealthTopic';
import Navbar from './components/Navigation/Navbar';
import HomeContent from './components/HomePage/HomeContent';
import ErrorFallback from './components/UI/ErrorFallback';

// Google Analytics initialize
ReactGA.initialize(`${process.env.REACT_APP_GOOGLE_ANALYTICS_ID}`, {
    testMode: process.env.NODE_ENV === 'test',
});

// Add license for Material UI X-Grid
LicenseInfo.setLicenseKey(`${process.env.REACT_APP_MUI_LICENSE}`);

const styles = {
    '&.MuiAppBar-root': {
        backgroundColor: muiTheme.palette.navbarBlue.main,
        zIndex: 0,
    },
    '&.MuiTab-root': {
        color: 'rgba(255, 255, 255, 0.6)',
    },
    '&.Mui-selected': {
        color: 'white',
    },
    '&.MuiTab-wrapped': {
        backgroundColor: 'black',
    },
};

const App = () => {
    // // State // //
    // App state will track React Router params
    const [homeLoading, setHomeLoading] = useState(true);
    const [routerParams, setRouterParams] = useState({});
    const [counties, setCounties] = useState([]);
    const [homeContentTopics, setHomeContentTopics] = useState(null);
    const [errorReset, setErrorReset] = useState(false);
    const [ncdmContent, setNCDMContent] = useState();
    const [ncdmSelections, setNCDMSelections] = useState({
        indicator: '',
        measure: '',
        year: '',
        id: '',
    });
    const [topicDrawerData, setTopicDrawerData] = useState(null);

    // // Hooks // //
    const handleError = useErrorHandler();

    // // Methods // //
    const resetNcdmValues = () => {
        setNCDMSelections({
            indicator: '',
            measure: '',
            year: '',
        });

        setNCDMContent();
    };

    const handleErrorReset = () => {
        setErrorReset(!errorReset);
    };

    const createAppBar = (incomingTopic) => {
        const { topicTitle, topicUrlPath, themes } =
            findTopicByUrlPath(homeContentTopics, incomingTopic) || {};

        return (
            <>
                <Grid
                    item
                    container
                    justifyContent="flex-start"
                    className={`topic-container`}
                >
                    {routerParams.theme ? (
                        <Link
                            className="unset-router-link topic-header-container"
                            to={`/${topicUrlPath}`}
                        >
                            <ChevronLeftIcon />
                            <p className="topic-header">{topicTitle}</p>
                        </Link>
                    ) : (
                        <p className="topic-header">{topicTitle}</p>
                    )}
                </Grid>

                {/* Navbar on a topic page */}
                <Grid
                    item
                    container
                    justifyContent="flex-start"
                    className="themes-container"
                >
                    <AppBar position="static" sx={styles}>
                        <Tabs
                            value={routerParams.theme || false} // mui Tabs does not accept `undefined` as value when none of the tabs are selected -- must use bool `false to avoid warnings
                            aria-label="themes-selection-bar"
                            TabIndicatorProps={{
                                sx: {
                                    backgroundColor: 'rgba(255,255,255, 0.8)',
                                    height: 5,
                                },
                            }}
                            variant="fullWidth"
                        >
                            {themes.map((topicTheme) => {
                                const {
                                    themePath,
                                    themeTitle,
                                    defaultTabPath,
                                } = topicTheme;
                                return (
                                    <Tab
                                        sx={styles}
                                        key={themePath}
                                        value={themePath}
                                        className={`theme-tab ${
                                            themePath === routerParams.theme
                                                ? 'selected-theme-tab'
                                                : null
                                        }`}
                                        to={`/${topicUrlPath}/${themePath}/${defaultTabPath}`}
                                        onClick={resetNcdmValues}
                                        component={Link}
                                        label={themeTitle}
                                    ></Tab>
                                );
                            })}
                        </Tabs>
                    </AppBar>
                </Grid>
            </>
        );
    };

    // Takes in either a topic or subtopic and builds the content and links:
    const createRouteContent = () => (
        <Grid
            container
            item
            xs={12}
            direction="column"
            alignItems="center"
            wrap="nowrap"
        >
            {routerParams.topic ? createAppBar(routerParams.topic) : null}

            <HealthTopic
                counties={counties}
                ncdmContent={ncdmContent}
                ncdmSelections={ncdmSelections}
                setNCDMContent={setNCDMContent}
                setNCDMSelections={setNCDMSelections}
                setRouterParams={setRouterParams}
            />
        </Grid>
    );

    // // Effects // //
    useEffect(() => {
        fetch(`${process.env.REACT_APP_EPHT_API}/config/GetMDCounties`)
            .then(handleFetchError)
            .then((res) => res.json())
            .then((json) => {
                const sortedJson = json.sort((a, b) => {
                    // Priority mapping (lower number = higher priority)
                    const priorities = { Nationwide: 0, Statewide: 1 };

                    // Get county priorities (default to 2 for regular counties)
                    const aPriority = priorities[a.county] ?? 2;
                    const bPriority = priorities[b.county] ?? 2;

                    // First compare by priority
                    if (aPriority !== bPriority) {
                        return aPriority - bPriority;
                    }

                    // Otherwise sort alphabetically
                    return a.county.localeCompare(b.county);
                });
                setCounties(sortedJson);
            })
            .catch(handleError);
    }, [handleError]);

    // Effect to fetch all topics for home page:
    useEffect(() => {
        const fetchHomeContent = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_EPHT_API}/config/topics/MinimalConfig`
                );
                const homeContentJson = await response.json();
                setHomeContentTopics(homeContentJson);
                setHomeLoading(false);
            } catch (error) {
                handleFetchError(error);
                setHomeLoading(false);
            }
        };
        fetchHomeContent();
    }, []);

    // Dedicated effect to set state variable used in TopicDrawerNavigationContext context provider:
    useEffect(() => {
        // Set the state variable supplied to the context provider in use by TopicsDrawer:
        if (routerParams.topic) {
            setTopicDrawerData(
                findTopicByUrlPath(homeContentTopics, routerParams.topic)
            );
        }
    }, [routerParams.topic, homeContentTopics]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={muiTheme}>
                <TopicDrawerNavigationContext.Provider value={topicDrawerData}>
                    <div className="root">
                        <Grid container justifyContent="center">
                            <Router basename={process.env.PUBLIC_URL}>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    wrap="nowrap"
                                    className="navbar"
                                >
                                    <Navbar
                                        routerParams={routerParams}
                                        handleErrorReset={handleErrorReset}
                                    />
                                </Grid>
                                {/* Don't attempt to render the routes until home has finished loading */}
                                {homeLoading ? null : (
                                    <ErrorBoundary
                                        FallbackComponent={ErrorFallback}
                                        resetKeys={[errorReset]}
                                    >
                                        <Switch>
                                            {/* Home route: */}
                                            <Route exact path="/">
                                                {homeContentTopics && (
                                                    <HomeContent
                                                        homeContentTopics={
                                                            homeContentTopics
                                                        }
                                                    />
                                                )}
                                            </Route>

                                            {/* Health topic routes: */}
                                            {homeContentTopics &&
                                                homeContentTopics.map(
                                                    ({
                                                        topicTitle,
                                                        topicUrlPath,
                                                        subtopics,
                                                    }) =>
                                                        subtopics ? (
                                                            subtopics.map(
                                                                ({
                                                                    topicTitle: subtopicTitle,
                                                                    topicUrlPath: subtopicUrlPath,
                                                                }) => (
                                                                    <Route
                                                                        key={
                                                                            subtopicTitle
                                                                        }
                                                                        path={`/${subtopicUrlPath}/:theme?/:tab?`}
                                                                        render={() =>
                                                                            createRouteContent()
                                                                        }
                                                                    ></Route>
                                                                )
                                                            )
                                                        ) : (
                                                            <Route
                                                                key={topicTitle}
                                                                path={`/${topicUrlPath}/:theme?/:tab?`}
                                                                render={() =>
                                                                    createRouteContent()
                                                                }
                                                            ></Route>
                                                        )
                                                )}

                                            {/* Non health topic routes should be placed here: */}
                                            {/*                  Routes                       */}

                                            {/* Catch-all to redirect home. Can be replaced with 404 if desired */}
                                            <Route path="/*">
                                                <Redirect to="/" />
                                            </Route>
                                        </Switch>
                                    </ErrorBoundary>
                                )}
                            </Router>
                        </Grid>
                    </div>
                </TopicDrawerNavigationContext.Provider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default App;
